/**
 * Admin Lead Pricing Management - List View with Bulk Edit
 * Configure lead pricing by category and subcategory
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, Settings, Plus, Edit2, Trash2, Save, X, 
  TrendingUp, Tag, Layers, CheckCircle, Check,
  Zap, BarChart3, RefreshCw, List, Grid, Square, CheckSquare
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

// List View Row Component
const PricingListRow = ({ config, isSelected, onSelect, onEdit, onDelete, isEditing, editValues, onEditChange }) => {
  if (isEditing) {
    return (
      <tr className="bg-blue-50 border-l-4 border-l-blue-500">
        <td className="px-4 py-3">
          <Checkbox checked={isSelected} onCheckedChange={onSelect} />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-blue-600" />
            <span className="font-medium">{config.category_name}</span>
            {config.subcategory_name && (
              <span className="text-gray-500">/ {config.subcategory_name}</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <Input
            type="number"
            value={editValues.base_price}
            onChange={(e) => onEditChange('base_price', parseFloat(e.target.value))}
            className="w-24 h-8"
            min="0"
          />
        </td>
        <td className="px-4 py-3">
          <select
            value={editValues.is_active ? 'active' : 'inactive'}
            onChange={(e) => onEditChange('is_active', e.target.value === 'active')}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={editValues.enable_quality_pricing}
              onCheckedChange={(checked) => onEditChange('enable_quality_pricing', checked)}
            />
            <span className="text-sm">Quality</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={editValues.enable_surge_pricing}
              onCheckedChange={(checked) => onEditChange('enable_surge_pricing', checked)}
            />
            <span className="text-sm">Surge</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-blue-600">Editing...</span>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-gray-50 border-b">
      <td className="px-4 py-3">
        <Checkbox checked={isSelected} onCheckedChange={onSelect} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-blue-600" />
          <span className="font-medium">{config.category_name}</span>
          {config.subcategory_name && (
            <span className="text-gray-500">/ {config.subcategory_name}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="font-semibold text-green-600">₹{config.base_price}</span>
      </td>
      <td className="px-4 py-3">
        {config.is_active ? (
          <Badge className="bg-green-100 text-green-800">Active</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        )}
      </td>
      <td className="px-4 py-3">
        {config.enable_quality_pricing ? (
          <Badge variant="outline" className="text-blue-600">
            <Check className="h-3 w-3 mr-1" />
            On
          </Badge>
        ) : (
          <span className="text-gray-400 text-sm">Off</span>
        )}
      </td>
      <td className="px-4 py-3">
        {config.enable_surge_pricing ? (
          <Badge variant="outline" className="text-orange-600">
            <Zap className="h-3 w-3 mr-1" />
            On
          </Badge>
        ) : (
          <span className="text-gray-400 text-sm">Off</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(config)}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(config.id)} className="text-red-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

// Bulk Edit Bar Component
const BulkEditBar = ({ selectedCount, onBulkEdit, onBulkDelete, onClearSelection }) => {
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [showPriceInput, setShowPriceInput] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <div className="bg-blue-600 text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-2">
        <CheckSquare className="h-5 w-5" />
        <span className="font-medium">{selectedCount} selected</span>
      </div>
      
      <div className="flex items-center gap-3 flex-wrap">
        {/* Bulk Price Update */}
        {showPriceInput ? (
          <div className="flex items-center gap-2 bg-white/20 rounded px-2 py-1">
            <span className="text-sm">₹</span>
            <input
              type="number"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              placeholder="New price"
              className="w-20 px-2 py-1 rounded text-gray-900 text-sm"
              min="0"
            />
            <Button 
              size="sm" 
              variant="secondary"
              onClick={() => {
                if (bulkPrice) {
                  onBulkEdit({ base_price: parseFloat(bulkPrice) });
                  setBulkPrice('');
                  setShowPriceInput(false);
                }
              }}
            >
              Apply
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowPriceInput(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setShowPriceInput(true)}>
            <DollarSign className="h-4 w-4 mr-1" />
            Set Price
          </Button>
        )}
        
        {/* Bulk Status Update */}
        <select
          value={bulkStatus}
          onChange={(e) => {
            if (e.target.value) {
              onBulkEdit({ is_active: e.target.value === 'active' });
              setBulkStatus('');
            }
          }}
          className="px-3 py-1.5 rounded text-gray-900 text-sm"
        >
          <option value="">Set Status...</option>
          <option value="active">Activate All</option>
          <option value="inactive">Deactivate All</option>
        </select>
        
        {/* Bulk Delete */}
        <Button size="sm" variant="destructive" onClick={onBulkDelete}>
          <Trash2 className="h-4 w-4 mr-1" />
          Delete
        </Button>
        
        {/* Clear Selection */}
        <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={onClearSelection}>
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );
};

// Add/Edit Pricing Modal Component
const AddPricingModal = ({ isOpen, onClose, onSave, categories, initialData }) => {
  const [formData, setFormData] = useState({
    category_id: '',
    category_name: '',
    subcategory_id: '',
    subcategory_name: '',
    base_price: 50,
    is_active: true,
    enable_quality_pricing: false,
    verified_lead_multiplier: 1.0,
    high_intent_multiplier: 1.0,
    enable_surge_pricing: false,
    surge_hours: [],
    surge_multiplier: 1.0
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const isEditing = Boolean(initialData?.id);

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        ...initialData,
        category_id: initialData.category_id || '',
        subcategory_id: initialData.subcategory_id || '',
      });
      const category = categories.find(c => c.id === initialData.category_id);
      setSelectedCategory(category);
    } else if (isOpen && !initialData) {
      setFormData({
        category_id: '',
        category_name: '',
        subcategory_id: '',
        subcategory_name: '',
        base_price: 50,
        is_active: true,
        enable_quality_pricing: false,
        verified_lead_multiplier: 1.0,
        high_intent_multiplier: 1.0,
        enable_surge_pricing: false,
        surge_hours: [],
        surge_multiplier: 1.0
      });
      setSelectedCategory(null);
    }
  }, [isOpen, initialData, categories]);

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setSelectedCategory(category);
    setFormData({
      ...formData,
      category_id: categoryId,
      category_name: category?.name || '',
      subcategory_id: '',
      subcategory_name: ''
    });
  };

  const handleSubcategoryChange = (subcategoryId) => {
    if (subcategoryId === '_none_') {
      setFormData({
        ...formData,
        subcategory_id: null,
        subcategory_name: null
      });
    } else {
      const subcategory = selectedCategory?.subcategories?.find(s => s.id === subcategoryId);
      setFormData({
        ...formData,
        subcategory_id: subcategoryId,
        subcategory_name: subcategory?.name || ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{isEditing ? 'Edit Lead Pricing' : 'Add Lead Pricing'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Category *</label>
            <select
              value={formData.category_id}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
              disabled={isEditing}
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Subcategory Selection */}
          {selectedCategory?.subcategories?.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1">Subcategory (Optional)</label>
              <select
                value={formData.subcategory_id || '_none_'}
                onChange={(e) => handleSubcategoryChange(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                disabled={isEditing}
              >
                <option value="_none_">All Subcategories (Category Level)</option>
                {selectedCategory.subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Base Price */}
          <div>
            <label className="block text-sm font-medium mb-1">Base Price per Lead (₹) *</label>
            <Input
              type="number"
              value={formData.base_price}
              onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
              min="0"
            />
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <label className="text-sm font-medium">Active</label>
          </div>

          {/* Quality Pricing Toggle */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span className="font-medium">Quality-Based Pricing</span>
              </div>
              <Checkbox
                checked={formData.enable_quality_pricing}
                onCheckedChange={(checked) => setFormData({ ...formData, enable_quality_pricing: checked })}
              />
            </div>
            {formData.enable_quality_pricing && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Verified Lead Multiplier</label>
                  <Input
                    type="number"
                    value={formData.verified_lead_multiplier}
                    onChange={(e) => setFormData({ ...formData, verified_lead_multiplier: parseFloat(e.target.value) })}
                    min="1"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">High Intent Multiplier</label>
                  <Input
                    type="number"
                    value={formData.high_intent_multiplier}
                    onChange={(e) => setFormData({ ...formData, high_intent_multiplier: parseFloat(e.target.value) })}
                    min="1"
                    step="0.1"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Surge Pricing Toggle */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-orange-600" />
                <span className="font-medium">Surge Pricing</span>
              </div>
              <Checkbox
                checked={formData.enable_surge_pricing}
                onCheckedChange={(checked) => setFormData({ ...formData, enable_surge_pricing: checked })}
              />
            </div>
            {formData.enable_surge_pricing && (
              <div className="mt-3">
                <label className="block text-xs text-gray-600 mb-1">Surge Multiplier</label>
                <Input
                  type="number"
                  value={formData.surge_multiplier}
                  onChange={(e) => setFormData({ ...formData, surge_multiplier: parseFloat(e.target.value) })}
                  min="1"
                  step="0.1"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={() => onSave(formData, !isEditing)} 
              className="flex-1"
              disabled={!formData.category_id}
            >
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? 'Save Changes' : 'Add Pricing'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const AdminLeadPricing = () => {
  const [categories, setCategories] = useState([]);
  const [pricingConfigs, setPricingConfigs] = useState([]);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pricing');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditValues, setInlineEditValues] = useState({});
  const { toast } = useToast();
  const { apiCall } = useAuth();

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const categoriesRes = await apiCall('/api/categories/');
      if (categoriesRes) {
        setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
      }

      const pricingRes = await apiCall('/api/lead-pricing/categories');
      if (pricingRes) {
        setPricingConfigs(pricingRes);
      }

      const settingsRes = await apiCall('/api/lead-pricing/settings');
      if (settingsRes) {
        setGlobalSettings(settingsRes);
      }

      const analyticsRes = await apiCall('/api/lead-pricing/analytics');
      if (analyticsRes) {
        setAnalytics(analyticsRes);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveGlobalSettings = async () => {
    try {
      await apiCall('/api/lead-pricing/settings', {
        method: 'PUT',
        body: JSON.stringify(globalSettings)
      });
      toast({ title: 'Success', description: 'Global settings saved' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const savePricingConfig = async (config, isNew = false) => {
    try {
      if (isNew) {
        await apiCall('/api/lead-pricing/categories', {
          method: 'POST',
          body: JSON.stringify(config)
        });
        toast({ title: 'Success', description: 'Pricing configuration created' });
      } else {
        await apiCall(`/api/lead-pricing/categories/${config.id}`, {
          method: 'PUT',
          body: JSON.stringify(config)
        });
        toast({ title: 'Success', description: 'Pricing configuration updated' });
      }
      setShowAddModal(false);
      setEditingConfig(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setShowAddModal(true);
  };

  const deletePricingConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this pricing configuration?')) return;
    
    try {
      await apiCall(`/api/lead-pricing/categories/${configId}`, {
        method: 'DELETE'
      });
      toast({ title: 'Success', description: 'Pricing configuration deleted' });
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(configId);
        return next;
      });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Bulk operations
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(pricingConfigs.map(c => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleBulkEdit = async (updates) => {
    if (selectedIds.size === 0) return;
    
    try {
      // Update each selected config
      const promises = Array.from(selectedIds).map(id => {
        const config = pricingConfigs.find(c => c.id === id);
        if (config) {
          return apiCall(`/api/lead-pricing/categories/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ ...config, ...updates })
          });
        }
        return Promise.resolve();
      });
      
      await Promise.all(promises);
      toast({ 
        title: 'Success', 
        description: `Updated ${selectedIds.size} pricing configuration(s)` 
      });
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} pricing configuration(s)?`)) return;
    
    try {
      const promises = Array.from(selectedIds).map(id => 
        apiCall(`/api/lead-pricing/categories/${id}`, { method: 'DELETE' })
      );
      
      await Promise.all(promises);
      toast({ 
        title: 'Success', 
        description: `Deleted ${selectedIds.size} pricing configuration(s)` 
      });
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Inline edit
  const startInlineEdit = (config) => {
    setInlineEditId(config.id);
    setInlineEditValues({
      base_price: config.base_price,
      is_active: config.is_active,
      enable_quality_pricing: config.enable_quality_pricing,
      enable_surge_pricing: config.enable_surge_pricing
    });
  };

  const handleInlineEditChange = (field, value) => {
    setInlineEditValues(prev => ({ ...prev, [field]: value }));
  };

  const saveInlineEdit = async () => {
    if (!inlineEditId) return;
    
    const config = pricingConfigs.find(c => c.id === inlineEditId);
    if (config) {
      await savePricingConfig({ ...config, ...inlineEditValues }, false);
      setInlineEditId(null);
      setInlineEditValues({});
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Lead Pricing Management</h1>
            <p className="text-gray-500 text-sm md:text-base">Configure pricing for leads by category and subcategory</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Add Pricing
          </Button>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Layers className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pricing Configs</p>
                    <p className="text-xl font-bold">{analytics.pricing_configs?.total || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Active Configs</p>
                    <p className="text-xl font-bold">{analytics.pricing_configs?.active || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg Lead Price</p>
                    <p className="text-xl font-bold">₹{analytics.price_statistics?.avg_price?.toFixed(0) || 50}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-xl font-bold">₹{(analytics.price_statistics?.total_revenue || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pricing">
              <List className="h-4 w-4 mr-2" />
              Category Pricing
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Global Settings
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Category Pricing Tab - LIST VIEW */}
          <TabsContent value="pricing" className="mt-6">
            {/* Bulk Action Bar */}
            <BulkEditBar
              selectedCount={selectedIds.size}
              onBulkEdit={handleBulkEdit}
              onBulkDelete={handleBulkDelete}
              onClearSelection={() => setSelectedIds(new Set())}
            />
            
            {pricingConfigs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">No pricing configurations</h3>
                  <p className="text-gray-500 mb-4">
                    Set up category-based pricing for leads
                  </p>
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Pricing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left w-10">
                            <Checkbox
                              checked={selectedIds.size === pricingConfigs.length && pricingConfigs.length > 0}
                              onCheckedChange={handleSelectAll}
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Price/Lead</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Quality</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Surge</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingConfigs.map(config => (
                          <PricingListRow
                            key={config.id}
                            config={config}
                            isSelected={selectedIds.has(config.id)}
                            onSelect={(checked) => handleSelectOne(config.id, checked)}
                            onEdit={handleEditConfig}
                            onDelete={deletePricingConfig}
                            isEditing={inlineEditId === config.id}
                            editValues={inlineEditValues}
                            onEditChange={handleInlineEditChange}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Global Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Global Lead Settings</CardTitle>
                <CardDescription>Default settings applied when no category-specific pricing exists</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {globalSettings && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Default Price per Lead (₹)</label>
                        <Input
                          type="number"
                          value={globalSettings.default_price_per_lead}
                          onChange={(e) => setGlobalSettings({
                            ...globalSettings,
                            default_price_per_lead: parseFloat(e.target.value)
                          })}
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Minimum Billing Amount (₹)</label>
                        <Input
                          type="number"
                          value={globalSettings.minimum_billing_amount}
                          onChange={(e) => setGlobalSettings({
                            ...globalSettings,
                            minimum_billing_amount: parseFloat(e.target.value)
                          })}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Billing Frequency</label>
                        <select
                          value={globalSettings.billing_frequency}
                          onChange={(e) => setGlobalSettings({
                            ...globalSettings,
                            billing_frequency: e.target.value
                          })}
                          className="w-full border rounded-md px-3 py-2"
                        >
                          <option value="per_lead">Per Lead</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Lead Rejection Window (Hours)</label>
                        <Input
                          type="number"
                          value={globalSettings.rejection_window_hours}
                          onChange={(e) => setGlobalSettings({
                            ...globalSettings,
                            rejection_window_hours: parseInt(e.target.value)
                          })}
                          min="0"
                        />
                      </div>
                    </div>

                    <Button onClick={saveGlobalSettings}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Global Settings
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Lead Pricing Analytics</CardTitle>
                <CardDescription>Revenue and usage statistics</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Min Price</p>
                        <p className="text-2xl font-bold">₹{analytics.price_statistics?.min_price || 0}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Max Price</p>
                        <p className="text-2xl font-bold">₹{analytics.price_statistics?.max_price || 0}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Total Leads</p>
                        <p className="text-2xl font-bold">{analytics.price_statistics?.total_leads || 0}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">With Quality Pricing</p>
                        <p className="text-2xl font-bold">{analytics.pricing_configs?.with_quality || 0}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No analytics data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Modal */}
        <AddPricingModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingConfig(null);
          }}
          onSave={savePricingConfig}
          categories={categories}
          initialData={editingConfig}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminLeadPricing;
